import React, { Fragment, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { useDispatch } from 'react-redux';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Input from '../ui/forms/fields/input';
import PrimaryButton from '../ui/buttons/primaryButton';

import playerService from '../../features/player/playerService';
import { setAlertError } from '../../features/alert/alertSlice';
import { getGameByEventId } from '../../features/game/gameSlice';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  surname: yup.string().required('Surname is required'),
  handicap: yup.number().typeError('Handicap must be a number'),
  bsaNumber: yup.string().optional()
});

const PlayerViewDialog = ({ player, event, open, setOpen }) => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: player
  });

  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    if (player) {
      setValue('name', player.name);
      setValue('surname', player.surname);
      setValue('email', player.email);
      setValue('handicap', player.handicap);
      setValue('bsaNumber', player.bsaNumber || '');
    }

    return () => {
      setSaving(false);
      resetForm();
    };
  }, [setSaving, player, setValue]);

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    reset(
      {
        name: '',
        surname: '',
        email: '',
        handicap: '',
        bsaNumber: ''
      },
      { keepValues: false, keepDirty: false }
    );
  };

  const onSubmit = async (data) => {
    setSaving(true);

    try {
      if (player) {
        data.id = player.id;
        await playerService.updatePlayerFromEvent(event.id, data);

        // The server rewrites the game and bracket snapshots too, so pull the
        // fresh copies instead of leaving the open views on the old name.
        dispatch(getGameByEventId(event.id));
        dispatch(getBracketsByEventId(event.id));
      } else {
        await playerService.addPlayerToEvent(event.id, data);
      }

      resetForm();
      handleClose();
    } catch (error) {
      const response = error?.response?.data;
      const message =
        response?.message ||
        (typeof response === 'string' ? response : '') ||
        error?.message ||
        'The player could not be saved.';
      dispatch(setAlertError(message));
    } finally {
      setSaving(false);
    }
  };

  const onAddCreate = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <Fragment>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography variant="h6" color="primary">
            Player Details
          </Typography>
        </DialogTitle>
        <Box sx={{ p: 2 }}>
          <Box display={'flex'} gap={2}>
            <Input label="Name*" {...register('name')} error={errors.name} focused={true} />
            <Input label="Surname*" {...register('surname')} error={errors.surname} />
            <Input
              label="Handicap"
              {...register('handicap')}
              error={errors.handicap}
              type="number"
              sx={{ maxWidth: 150 }}
            />
          </Box>

          <Box display={'flex'} gap={2} mt={2}>
            <Input label="BSA Number" {...register('bsaNumber')} error={errors.bsaNumber} />
          </Box>
        </Box>
        <DialogActions>
          {saving ? (
            <CircularProgress />
          ) : (
            <Fragment>
              <PrimaryButton onClick={() => handleClose()}>Cancel</PrimaryButton>
              <PrimaryButton onClick={() => onAddCreate()} variant={'contained'}>
                Save
              </PrimaryButton>
            </Fragment>
          )}
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default PlayerViewDialog;
